import { useEffect, useState, useMemo } from "react";
import apiClient from "@/lib/apiClient";

import { BookOpen, Search, ChevronRight, ExternalLink, Pill, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Couleurs par catégorie clinique
const CATEGORY_COLORS = {
  "Cardiovasculaire": "bg-rose-50 text-rose-700 border-rose-200",
  "Endocrinologie": "bg-amber-50 text-amber-700 border-amber-200",
  "Endocrinologie / Os": "bg-amber-50 text-amber-700 border-amber-200",
  "Neurocognitif": "bg-violet-50 text-violet-700 border-violet-200",
  "Psychiatrie": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Psychiatrie / Sommeil": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Neurologie": "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  "Neurologie / Douleur": "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  "Gastro-entérologie": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Pneumologie": "bg-sky-50 text-sky-700 border-sky-200",
  "Urologie": "bg-teal-50 text-teal-700 border-teal-200",
  "Douleur": "bg-orange-50 text-orange-700 border-orange-200",
  "Rhumatologie": "bg-yellow-50 text-yellow-700 border-yellow-200",
};

export default function HASReference() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/has-reference`)
      .then((r) => {
        setData(r.data);
        setLoading(false);
      })
      .catch((e) => {
        console.error("Erreur référentiel HAS:", e);
        setLoading(false);
      });
  }, []);

  // Pathologies filtrées par recherche
  const filteredPathologies = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.pathologies;
    return data.pathologies.filter((p) => {
      if (p.label.toLowerCase().includes(q)) return true;
      if (p.category.toLowerCase().includes(q)) return true;
      return p.medications.some(
        (m) =>
          m.dci.toLowerCase().includes(q) ||
          (m.category || "").toLowerCase().includes(q) ||
          (m.indications || []).some((i) => i.toLowerCase().includes(q)),
      );
    });
  }, [data, search]);

  // Grouper par catégorie clinique
  const grouped = useMemo(() => {
    const out = {};
    filteredPathologies.forEach((p) => {
      if (!out[p.category]) out[p.category] = [];
      out[p.category].push(p);
    });
    return out;
  }, [filteredPathologies]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="has-reference-loading">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto" data-testid="has-reference-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
            <BookOpen className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Référentiel HAS — Indications thérapeutiques</h1>
            <p className="text-sm text-slate-600">
              Indications validées par pathologie selon RCP officiels (BDPM/ANSM), HAS et sociétés savantes françaises
            </p>
          </div>
        </div>
        {data && (
          <div className="flex gap-2 mt-3">
            <Badge className="bg-teal-100 text-teal-700 border border-teal-300" data-testid="has-stat-pathologies">
              {data.total_pathologies} pathologies de référence
            </Badge>
            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid="has-stat-medications">
              {data.total_medications} médicaments validés
            </Badge>
          </div>
        )}
      </div>

      {/* Barre de recherche */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Rechercher une pathologie, un médicament (DCI), une classe…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-11"
          data-testid="has-search-input"
        />
      </div>

      {/* Grille pathologies par catégorie */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(grouped).map(([category, paths]) => (
          <Card key={category} className="border-slate-200" data-testid={`has-category-${category.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center justify-between">
                <span className={`px-2.5 py-1 rounded-md text-xs border ${CATEGORY_COLORS[category] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                  {category}
                </span>
                <span className="text-xs text-slate-500 font-normal">{paths.length}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-1.5">
              {paths.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelected(p)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between group"
                  data-testid={`has-pathology-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{p.label}</p>
                    <p className="text-xs text-slate-500">
                      {p.medication_count} médicament{p.medication_count > 1 ? "s" : ""} validé{p.medication_count > 1 ? "s" : ""}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-teal-600 flex-shrink-0" />
                </button>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPathologies.length === 0 && (
        <div className="text-center py-12 text-slate-500" data-testid="has-no-results">
          Aucun résultat pour « {search} »
        </div>
      )}

      {/* Modal détail pathologie */}
      {selected && (
        <PathologyDetailModal
          pathology={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function PathologyDetailModal({ pathology, onClose }) {
  // Grouper les médicaments par classe pharmacologique
  const byClass = useMemo(() => {
    const out = {};
    (pathology.medications || []).forEach((m) => {
      const cls = m.category || "Autre";
      if (!out[cls]) out[cls] = [];
      out[cls].push(m);
    });
    return out;
  }, [pathology]);

  // Calculer les indications cumulées depuis la liste de meds (utile pour avoir une vue agrégée)
  const cumulatedIndications = useMemo(() => {
    const set = new Set();
    (pathology.medications || []).forEach((m) => {
      (m.indications || []).forEach((i) => set.add(i));
    });
    return Array.from(set).sort();
  }, [pathology]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="pathology-detail-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-t-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Badge className={`mb-2 text-xs border ${CATEGORY_COLORS[pathology.category] || "bg-slate-100"}`}>
                {pathology.category}
              </Badge>
              <h2 className="text-xl font-bold text-slate-900" data-testid="pathology-detail-title">
                {pathology.label}
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                {pathology.medication_count} médicament{pathology.medication_count > 1 ? "s" : ""} validé{pathology.medication_count > 1 ? "s" : ""}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-pathology-detail-btn">
              ✕
            </Button>
          </div>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1">
          {/* Sources */}
          <div className="mb-6 p-3 bg-teal-50 border border-teal-200 rounded-lg">
            <p className="text-xs font-semibold text-teal-800 mb-1">Sources de référence</p>
            <p className="text-xs text-teal-700">{pathology.references}</p>
          </div>

          {/* Indications validées */}
          {cumulatedIndications.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Indications HAS / RCP couvertes</h3>
              <div className="flex flex-wrap gap-1.5">
                {cumulatedIndications.map((i, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {i}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Médicaments groupés par classe */}
          <div>
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Médicaments validés par classe</h3>
            <div className="space-y-3">
              {Object.entries(byClass).map(([cls, meds]) => (
                <div key={cls} className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                    <p className="text-xs font-semibold text-slate-700">{cls}</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {meds.map((m) => (
                      <div key={m.dci} className="px-3 py-2 hover:bg-slate-50" data-testid={`med-${m.dci}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Pill className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                            <span className="text-sm font-medium text-slate-800 capitalize truncate">{m.dci}</span>
                            {m.atc && (
                              <span className="text-[10px] text-slate-400 font-mono">{m.atc}</span>
                            )}
                          </div>
                          <a
                            href={`https://base-donnees-publique.medicaments.gouv.fr/index.php#result?affiner=true&page=1&recherche=${encodeURIComponent(m.dci)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1 flex-shrink-0"
                            title="Consulter le RCP officiel sur la BDPM"
                          >
                            BDPM <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                        {m.indications?.length > 0 && (
                          <ul className="mt-1 ml-5 text-xs text-slate-600 list-disc list-inside">
                            {m.indications.slice(0, 3).map((i, idx) => (
                              <li key={idx}>{i}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
          <Button onClick={onClose} variant="outline" data-testid="close-pathology-detail-bottom-btn">
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
}
